import React from "react"
import { type IntervalOrigin, type ViewKey } from "@digits-graphql/frontend/graphql-bearer"
import { ScopeProviderWithDebugAtoms } from "@digits-shared/atoms/ScopeProviderWithDebugAtoms"
import { usePropagateValueToAtom } from "@digits-shared/atoms/usePropagateValueToAtom"
import { JWTPermissionFlag } from "@digits-shared/session/jwt/jwtPermissions"
import { createScope, molecule } from "bunshi"
import { use } from "bunshi/react"
import { atom, useSetAtom } from "jotai"
import { useBlockingViewVersion } from "src/frontend/components/OS/Applications/Shared/Accounting/hooks/useBlockingViewVersion"
import { useVendorCustomerCustomization } from "src/frontend/components/OS/Applications/VendorDirectory/hooks/useVendorCustomerCustomization"
import { useHasPermission } from "src/frontend/components/Shared/Permissions/Requires"
import {
  FrontendPermissionModule,
  FrontendPermissionSource,
} from "src/frontend/session/permissionModule"
import useIntervalOrigin from "src/shared/hooks/useIntervalOrigin"

const canCreateTransactionsAtom = atom(false)
const canUpdateTransactionsAtom = atom(false)
const intervalOriginAtom = atom<IntervalOrigin>(null as unknown as IntervalOrigin)
const viewKeyAtom = atom<ViewKey>(null as unknown as ViewKey)

export const vendorDirectoryMolecule = molecule(() => {
  use(VendorDirectoryScope)

  return {
    canCreateTransactionsAtom,
    canUpdateTransactionsAtom,
    intervalOriginAtom,
    viewKeyAtom,
  }
})

export const VendorDirectoryScope = createScope<unknown>(undefined, {
  debugLabel: "VendorDirectoryScope",
})

export const VendorDirectoryScopeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { isCustomers } = useVendorCustomerCustomization()

  usePropagateValueToAtom({
    updateAtom: useSetAtom(intervalOriginAtom),
    value: useIntervalOrigin(),
  })
  usePropagateValueToAtom({
    updateAtom: useSetAtom(canUpdateTransactionsAtom),
    value: useCanUpdateTransactions(),
  })
  usePropagateValueToAtom({
    updateAtom: useSetAtom(canCreateTransactionsAtom),
    value: useCanCreateTransactions(),
  })
  usePropagateValueToAtom({
    updateAtom: useSetAtom(viewKeyAtom),
    value: useBlockingViewVersion(),
  })

  return (
    <ScopeProviderWithDebugAtoms scope={VendorDirectoryScope} value={isCustomers}>
      {children}
    </ScopeProviderWithDebugAtoms>
  )
}

const useCanUpdateTransactions = () =>
  useHasPermission({
    source: FrontendPermissionSource.LegalEntity,
    module: FrontendPermissionModule.Transactions,
    flag: JWTPermissionFlag.Update,
  })

const useCanCreateTransactions = () =>
  useHasPermission({
    source: FrontendPermissionSource.LegalEntity,
    module: FrontendPermissionModule.Transactions,
    flag: JWTPermissionFlag.Create,
  })
